import { useState } from 'react';
import {
  miceDateWindow,
  type MiceEventDays,
  type MiceNeedAlert,
  type MiceNeedStatus,
  type MiceNeedView,
  type MiceProjectView,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Input } from '@web/ui/core';
import { formatLegDay, formatShortDay } from './need_format';
import { DrawerSection } from './need_recommendation';

export type Direction = 'OUTWARD' | 'RETURN';

/** Status em que o servidor aceita mudar a data (`POST /needs/:needId/date`); `ordered` só sem
 *  pedido vivo no sentido, o que o servidor decide (409). */
const DATE_CHANGEABLE: ReadonlySet<MiceNeedStatus> = new Set<MiceNeedStatus>([
  'recommended',
  'no_solution',
  'approved',
  'ordered',
]);

const DIRECTIONS = {
  round_trip: ['OUTWARD', 'RETURN'],
  outbound_only: ['OUTWARD'],
  return_only: ['RETURN'],
} as const satisfies Record<MiceNeedView['tripKind'], readonly Direction[]>;

const LABEL = { OUTWARD: 'mice.table.outbound', RETURN: 'mice.table.inbound' } as const;
const CHANGE = {
  OUTWARD: 'mice.needs.dates.changeOutbound',
  RETURN: 'mice.needs.dates.changeInbound',
} as const;
const FIELD = {
  OUTWARD: 'mice.needs.dates.fieldOutbound',
  RETURN: 'mice.needs.dates.fieldInbound',
} as const;

const dateOf = (need: MiceNeedView, direction: Direction): string | null =>
  direction === 'OUTWARD' ? need.outboundDate : need.returnDate;

/** Quem compra muda a data com a versão vigente na mão (o servidor confere a versão). */
export const canChangeDate = (need: MiceNeedView, canPurchase: boolean): boolean =>
  canPurchase && need.recommendation !== null && DATE_CHANGEABLE.has(need.status);

/** O alerta de pedido de outro dia ("Ida: … outra data"): o sentido que ele cita, para o atalho
 *  "Mudar data da ida" ao lado dele. */
export function dateAlertDirection(alert: MiceNeedAlert): Direction | null {
  if (alert.code !== 'out_of_scope_request' || !alert.message.includes('outra data')) return null;
  if (alert.message.startsWith('Ida:')) return 'OUTWARD';
  return alert.message.startsWith('Volta:') ? 'RETURN' : null;
}

/** As datas do evento que limitam a troca; sem a data do evento, nenhuma. */
export const eventDaysOf = (
  project: Pick<MiceProjectView, 'eventDate' | 'eventEnd' | 'premises'> | undefined,
): MiceEventDays | null =>
  project?.eventDate
    ? { eventDate: project.eventDate, eventEnd: project.eventEnd, premises: project.premises }
    : null;

/** O que o campo aceita: a janela do evento (a mesma do servidor). Com o pedido do viajante por
 *  outro dia no sentido, só o início do evento limita: o servidor aceita o dia que ele pediu. */
interface DateLimits {
  window: { from: string; to: string };
  asked: boolean;
}

function limitsOf(
  need: MiceNeedView,
  event: MiceEventDays | null,
  direction: Direction,
): DateLimits | null {
  if (!event) return null;
  const asked = (need.recommendation?.alerts ?? []).some(
    (a) => dateAlertDirection(a) === direction,
  );
  return { window: miceDateWindow(direction, event), asked };
}

interface NeedDatesProps {
  need: MiceNeedView;
  /** Datas do evento; sem elas, o campo não limita (o servidor recusa). */
  event: MiceEventDays | null;
  canChange: boolean;
  busy: boolean;
  /** Sentido com o campo de data aberto (o alerta de outra data também o abre). */
  editing: Direction | null;
  onEdit: (direction: Direction | null) => void;
  onChange: (direction: Direction, date: string, onDone: () => void) => void;
}

function DateForm({
  direction,
  initial,
  limits,
  busy,
  onCancel,
  onSubmit,
}: Readonly<{
  direction: Direction;
  initial: string;
  limits: DateLimits | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (date: string) => void;
}>) {
  const [date, setDate] = useState(initial);
  const range = limits?.window;
  const free = limits?.asked === true;
  return (
    // min/max e o hint mostram a janela; quem decide é o servidor (o dia pedido sem alerta passa).
    <form
      noValidate
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(date);
      }}
    >
      <div className="flex items-center gap-2">
        <Input
          type="date"
          aria-label={t(FIELD[direction])}
          value={date}
          min={range && !(free && direction === 'OUTWARD') ? range.from : undefined}
          max={range && !(free && direction === 'RETURN') ? range.to : undefined}
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />
        <Button size="sm" type="submit" disabled={busy || date === '' || date === initial}>
          {t('mice.needs.dates.submit')}
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
          {t('mice.needs.dates.cancel')}
        </Button>
      </div>
      {range ? (
        <p className="text-micro text-muted-foreground">
          {t(free ? 'mice.needs.dates.windowAsked' : 'mice.needs.dates.window', {
            from: formatShortDay(range.from),
            to: formatShortDay(range.to),
          })}
        </p>
      ) : null}
      <p className="text-micro text-muted-foreground">{t('mice.needs.dates.hint')}</p>
    </form>
  );
}

/** As datas da viagem por sentido; quem compra muda a de um sentido e o agente busca de novo. */
export function NeedDates({
  need,
  event,
  canChange,
  busy,
  editing,
  onEdit,
  onChange,
}: Readonly<NeedDatesProps>): React.JSX.Element {
  return (
    <DrawerSection title={t('mice.needs.dates.title')}>
      <ul className="flex flex-col gap-2">
        {DIRECTIONS[need.tripKind].map((direction) => {
          const date = dateOf(need, direction);
          return (
            <li key={direction} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 text-meta">
                <span className="font-medium text-foreground">{t(LABEL[direction])}</span>
                <span className="tabular-nums text-foreground">
                  {date ? formatLegDay(date) : '—'}
                </span>
                {canChange && editing !== direction ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      onEdit(direction);
                    }}
                  >
                    {t(CHANGE[direction])}
                  </Button>
                ) : null}
              </div>
              {canChange && editing === direction ? (
                <DateForm
                  direction={direction}
                  initial={date ?? ''}
                  limits={limitsOf(need, event, direction)}
                  busy={busy}
                  onCancel={() => {
                    onEdit(null);
                  }}
                  onSubmit={(next) => {
                    onChange(direction, next, () => {
                      onEdit(null);
                    });
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </DrawerSection>
  );
}
